import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, Select, Icon, Popover, Switch, message, Tooltip } from 'antd';
import { autoTransform, extractUnsupportedCharacters } from '../../utils/helper';
import {
  ModalBody,
  ModalContainer,
  InputLabel,
  Container,
  InputContainer,
  PopupContainer,
  PopupActionContainer,
  PopupLabel,
  SaveButton,
  CancelButton,
  AssetAction,
  WarningLabel,
} from './styles';
import { getAllCategories, createCategory } from '../../utils/services/categoriesAPI';
import { AppStoreContext } from '../../context/appStoreContext';
import { getGroupPermission } from '../../utils/services/teamsAPI';
import { CREATE_REUSABLE_ASSET } from './translationValues';
import { useTranslation } from 'react-i18next';
import { createStandaloneBlock, createStandaloneFlow, getStandaloneBlock } from '../../utils/services/complexDocumentsAPI';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { DYNAMIC_TABLE_BLOCK, STATIC_TABLE_BLOCK, FOOTER, HEADER, STANDALONE_BLOCK, dummyCallback } from '../../utils/complexDocumentConstants';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { deepCopy } from '../../utils/javascript';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { createEmailReferencedBlock, getEmailStandaloneBlock } from '../../utils/services/complexEmailAPI';
import { AddNewCategory, CategoryInputLabel, InputFlexColumn } from '../CreateNewTemplate/styles';

const { Option } = Select;

const NAMESPACES = ['CREATE_REUSABLE_ASSET'];

const CreateReusableAsset = props => {
    const { onSucess, type = 'flow', isEmailTemplate } = props;
    const { t } = useTranslation(NAMESPACES);
    const { userDetails } = useContext(AppStoreContext);
    const userType = userDetails.userType;

    const [templateName, setTemplateName] = useState('');
    const [validTempName, setValidTempName] = useState(false);
    const [validCategName, setValidCategName] = useState(false);
    const [allCategories, setAllCategories] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState(null);
    const [savePopupVisible, setSavePopupVisible] = useState(false);
    const [savingCategory, setSavingCategory] = useState(false);
    const [warningText, setWarningText] = useState('');
    const [allTeams, setAllTeams] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [savingLoader, setSavingLoader] = useState(false);
    const [isAttach, setIsAttach] = useState(false);
    const [blockType, setBlockType] = useState('content');

    const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
    const [selectedBlocks, updateArtboard] = useArtboardContext(
      selectedBlockSelector,
      `reusableTemplate`,
    );

    const selectedBlocksParentSelector = useCallback(
      store => store[CONTEXT_KEYS.SELECTED_BLOCKS_PARENT],
      [],
    );
    const [selectedBlocksParent] = useArtboardContext(
      selectedBlocksParentSelector,
      `reusableTemplate`,
    );

    const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
    const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

    const newFlowDataSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA], []);
    const [flow, methods] = useDocumentContext(newFlowDataSelector, 'ComplexArtboard');

    useEffect(() => {
        (async () => {
            const response = await getAllCategories(true);
            if (response && response.data) {
                let loggedUserCategory = response.data.filter(
                    categoryObj => userType === categoryObj.categoryusertype,
                );
                setAllCategories(loggedUserCategory);
            }
            const teamData = await getGroupPermission(true);
            if (teamData?.data) {
                if (teamData.data.length === 1) {
                    setSelectedGroup(teamData.data[0].rgId);
                }

                setAllTeams(teamData.data);
            }
        })();
        // eslint-disable-next-line
    }, []);

    const saveNewFlow = async () => {
        let newFlow = {
          categoryId: selectedCategoryId,
          resourceGroupId: selectedGroup,
          language: methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE],
        };

        newFlow.flow = {
            ...flow,
            flowName: templateName,
        isStandalone: true,
        orderNumber: 1,
        style: {
          ...flow.style,
          fontFamily: defaultStyle.fontFamily,
          fontSize: defaultStyle.fontSize,
        }

      };
      setSavingLoader(true);
      const response = await createStandaloneFlow(newFlow);
      if (response.statusCode === 2601) {
        onSucess(response.data.id, isAttach, flow.uuid);
        message.success(t(CREATE_REUSABLE_ASSET.NEW_PAGE_CREATED_SUCCESS_MESSAGE.key, CREATE_REUSABLE_ASSET.NEW_PAGE_CREATED_SUCCESS_MESSAGE.value));
      } else {
        const error = response?.errors?.[0];

        if (error?.code === 5601) {
          const unsupportedChars = extractUnsupportedCharacters(error.description);

          message.error(
            t(
              CREATE_REUSABLE_ASSET.PAGE_NAME_UNSUPPORTED_CHARS.key,
              CREATE_REUSABLE_ASSET.PAGE_NAME_UNSUPPORTED_CHARS.value,
              { char: unsupportedChars }
            )
          );

        } else if (error?.code === 2601) {
          message.error(
            t(
              CREATE_REUSABLE_ASSET.PAGE_NAME_ALREADY_EXIST_ERROR_MESSAGE.key,
              CREATE_REUSABLE_ASSET.PAGE_NAME_ALREADY_EXIST_ERROR_MESSAGE.value,
            ),
          );

        } else {
          message.error(
            t(
              CREATE_REUSABLE_ASSET.CREATEION_NEW_PAGE_ERROR_MESSAGE.key,
              CREATE_REUSABLE_ASSET.CREATEION_NEW_PAGE_ERROR_MESSAGE.value,
            ),
          );
        }
      }
        setSavingLoader(false);
    };

    const setCategoryName = categoryName => {
        setNewCategoryName(categoryName.trimStart());
        if (categoryName.split(" ").join("").length) {
            setValidCategName(true);
        }
        else {
            setValidCategName(false)
        }
    };

    const handlePopupVisibleChange = visible => {
        setSavePopupVisible(visible);
    };

    const saveNewCategory = () => {
        setSavingCategory(true);
        let payload = {
            name: newCategoryName,
            type: 'element',
        };
        setSavePopupVisible(false);
        createNewCategory(payload);
    };

    const createNewCategory = async payload => {
        let categoryResponse = await createCategory(payload);
        if (categoryResponse && categoryResponse.errors && categoryResponse.errors.length === 0) {
            const savedCategory = {
                createdBy: null,
                createdOn: null,
                elements: null,
                id: categoryResponse.id,
                name: payload.name,
                parentId: 0,
                status: 'active',
                type: 'element',
                updatedBy: null,
                updatedOn: null,
            };
            setAllCategories(allCategories => [...allCategories, savedCategory]);
            setSavePopupVisible(false);
            setSelectedCategoryId(categoryResponse.id);
            setNewCategoryName(null);
            setSavingCategory(false);
        } else {
            setSavingCategory(false);
        }
    };

    const changeTempName = name => {
        setTemplateName(name.trimStart())
        if (name.split(" ").join("").length) {
            setWarningText('');
            setValidTempName(true);
        }
        else {
            setWarningText(t(CREATE_REUSABLE_ASSET.ENTER_VALID_NAME_ERROR_MESSAGE.key, CREATE_REUSABLE_ASSET.ENTER_VALID_NAME_ERROR_MESSAGE.value));
            setValidTempName(false)
        }
    };

    const OnSelectAttach = () => {
        setIsAttach(!isAttach);
    }

    useEffect(() => {
      if (selectedBlocks.length === 1) {
        const blockType = methods.get()[selectedBlocks[0]].type;
        [HEADER, FOOTER].includes(blockType) ? setBlockType(blockType) : setBlockType('content');
      }
    }, [methods, selectedBlocks]);

    const deleteSelectedBlocks = useCallback(() => {
      blockType === 'content' &&
        methods.setPage('deleteMultipleBlocks', {
          parentUUID: selectedBlocksParent,
          blocks: [...selectedBlocks],
          pageId: `${selectedBlocksParent}-0`,
          flowUUID: selectedBlocksParent,
          uuid: selectedBlocksParent,
        });

      [HEADER, FOOTER].includes(blockType) &&
        methods.setPage('deleteStaticElement', {
          ...deepCopy(methods.get()[selectedBlocks[0]]),
          uuid: selectedBlocksParent,
        });
    }, [blockType, methods, selectedBlocks, selectedBlocksParent]);

    const processBlock = (block, orderIndex) => {
      if (block.blockType === DYNAMIC_TABLE_BLOCK) {
        if (block.blockDTOList) {
          block.blockDTOList.forEach(childBlockObj => {
            delete childBlockObj.blockId;
          });
        } else {
          const tableProperties = block.style.tableProperties;
          const tableChildBlock = [tableProperties.topBlock, tableProperties.bottomBlock];
          const dtoList = tableChildBlock.filter(element => element !== undefined);
          block.blockDTOList = dtoList.map(childBlockUUID => {
            const childBlock = Object.assign({}, methods.get()[childBlockUUID]);
            delete childBlock.blockId;
            delete childBlock.isChild;
            delete childBlock.parentBlockUUID;
            delete childBlock.isStandalone;
            return childBlock;
          });
        }
      } else if (block.blockType === STATIC_TABLE_BLOCK) {
        block.contents = block.contents.map(cell => {
          cell.contents = methods.get()[cell.uuid] ? [Object.assign({}, methods.get()[cell.uuid])] : [];
          return cell;
        });
      }
      delete block.blockId;
      delete block.orderNumber;
      block.isStandalone = false;
      block.orderNumber = orderIndex;
      return block;
    };

    const convertToStandaloneBlocks = async newReusableSectionId => {
      try {
        let getStandaloneBlockApi = getStandaloneBlock;
        if (isEmailTemplate) {
          getStandaloneBlockApi = getEmailStandaloneBlock;
        }

        const standaloneBlockResponse = await getStandaloneBlockApi(
          newReusableSectionId,
          undefined,
          false,
        );

        if (standaloneBlockResponse.statusCode !== 2505) {
          if (!standaloneBlockResponse.errors?.[0]?.description) {
            message.error(
              t(
                CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.key,
                CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.value,
              ),
            );
          }
          return;
        }

        let indexOfFirstSelectedBlock, dropItem;
        const { blocks, parentBlockId, variableContractDTOS } = standaloneBlockResponse.data;
        if (blockType === 'content') {
          indexOfFirstSelectedBlock = methods
            .get()
            [selectedBlocksParent][`${selectedBlocksParent}-0`].indexOf(selectedBlocks[0]);
          dropItem =
            indexOfFirstSelectedBlock === 0
              ? methods.get()[selectedBlocksParent][`${selectedBlocksParent}-0`][
                indexOfFirstSelectedBlock + selectedBlocks.length
              ]
              : methods.get()[selectedBlocksParent][`${selectedBlocksParent}-0`][
                  indexOfFirstSelectedBlock - 1
                ];
        }
        const blocks_data = blocks.map(obj => {
          const newObj = { ...obj };
          newObj.blockId = [HEADER, FOOTER].includes(blockType) ? parentBlockId : newObj.blockId;

          if(obj.blockType === DYNAMIC_TABLE_BLOCK){
            obj.blockDTOList.forEach(list=>{
              list.contents.forEach(content => {
                delete content.uuid;
               });
            })
          } else if (obj.blockType === STATIC_TABLE_BLOCK) {
            obj.contents.forEach(cell => {
              cell.contents = [methods.get()[cell.uuid]];
            });
          }

          if (obj.contents) {
            obj.contents.forEach(content => {
              delete content.uuid;
            });
          }

          newObj.type = newObj.blockType;
          delete newObj.orderNumber;
          return newObj;
        });
        const parentBlock = deepCopy(methods.get()[selectedBlocksParent]);
        const { updatedBlocks } = autoTransform(
          blocks_data,
          isEmailTemplate ? { width: parentBlock.width, height: 1 } : parentBlock,
          isEmailTemplate ? 590 : undefined,
          isEmailTemplate ? 1 : undefined,
        );
        const standaloneBlockTemplate = [HEADER, FOOTER].includes(blockType)
          ? Object.assign({
              block: updatedBlocks[0],
              uuid: selectedBlocksParent,
              type: blockType,
            })
          : Object.assign({
              blockId: parentBlockId,
              variableContract: variableContractDTOS,
              uuid: selectedBlocksParent,
              pageId: `${selectedBlocksParent}-0`,
              dropType: indexOfFirstSelectedBlock === 0 ? 'top' : 'bottom',
              dropItem: dropItem,
              block: {
                blockId: parentBlockId,
                blockType: STANDALONE_BLOCK,
                type: STANDALONE_BLOCK,
                blockDTOList: updatedBlocks,
                isStandalone: true,
                variableContract: variableContractDTOS,
              },
            });

        deleteSelectedBlocks();

        blockType === 'content'
          ? methods.setPage('addBlock', standaloneBlockTemplate)
          : methods.setPage('addStaticElement', standaloneBlockTemplate);
      } catch (e) {
        console.error('Error occurred:', e);
        message.error(
          t(
            CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.key,
            CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.value,
          ),
        );
      }
    };

    const saveNewSection = async () => {
      try {
        let selectedSections = [];
        let orderIndex = 0;

        for (const blockId of selectedBlocks) {
          const block = deepCopy(methods.get()[blockId]);
          if (block.type === 'parent') {
            for (const contentUUID of block.contents) {
              const childBlock = deepCopy(methods.get()[contentUUID]);
              orderIndex++;
              selectedSections.push(processBlock(childBlock, orderIndex));
            }
          } else {
            orderIndex++;
            selectedSections.push(processBlock(block, orderIndex));
          }
        }

        const { isTransformed, updatedBlocks } = autoTransform(
          selectedSections,
          isEmailTemplate ? { width: 590, height: 1 } : undefined,
          methods.get()[selectedBlocksParent].width,
          isEmailTemplate ? 1 : methods.get()[selectedBlocksParent].height,
        );

        if (!isTransformed) {
          message.warn(
            t(
              CREATE_REUSABLE_ASSET.HEIGHT_DOES_NOT_FIT_CONTENT.key,
              CREATE_REUSABLE_ASSET.HEIGHT_DOES_NOT_FIT_CONTENT.value,
            ),
          );
          return;
        }
        const newReusableBlockPayload = Object.assign({
          categoryId: selectedCategoryId,
          resourceGroupId: selectedGroup,
          parentBlockName: templateName,
          isReferenced: true,
          blocks: updatedBlocks,
          type: blockType,
          language: methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE],
          style:{
            fontFamily: defaultStyle.fontFamily,
            fontSize: defaultStyle.fontSize,
            ...(isEmailTemplate && {width: 590})
          }
        });

        let createStandaloneBlockApi = createStandaloneBlock;
        if (isEmailTemplate) {
          createStandaloneBlockApi = createEmailReferencedBlock;
        }

        const response = await createStandaloneBlockApi(newReusableBlockPayload);
        if (response?.statusCode !== 2501) {
          const error = response?.errors?.[0];

          if (error?.code === 5601) {
            const unsupportedChars = extractUnsupportedCharacters(error.description);

            message.error(
              t(
                CREATE_REUSABLE_ASSET.SECTION_NAME_UNSUPPORTED_CHARS.key,
                CREATE_REUSABLE_ASSET.SECTION_NAME_UNSUPPORTED_CHARS.value,
                { char: unsupportedChars }
              )
            );
            return;
          }

          if (!error?.description) {
            message.error(
              t(
                CREATE_REUSABLE_ASSET.SECTION_CREATION_ERROR_MESSAGE.key,
                CREATE_REUSABLE_ASSET.SECTION_CREATION_ERROR_MESSAGE.value,
              ),
            );
          }

          return;
        }
        if (isAttach) {
          convertToStandaloneBlocks(response.data.id);
        }
        updateArtboard({
          [CONTEXT_KEYS.REUSABLE_ASSET_MODAL]: false,
          [CONTEXT_KEYS.SELECTED_BLOCKS]: [],
          [CONTEXT_KEYS.SELECTED_BLOCKS_PARENT]: '',
          [CONTEXT_KEYS.HEADER_OR_FOOTER_BLOCK_SELECTED]: false,
          [CONTEXT_KEYS.CONTENT_BLOCK_SELECTED]: false,
        });
        methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]: {} });
        message.success(
          t(
            CREATE_REUSABLE_ASSET.SECTION_CREATION_SUCCESS_MESSAGE.key,
            CREATE_REUSABLE_ASSET.SECTION_CREATION_SUCCESS_MESSAGE.value,
          ),
        );
      } catch (error) {
        console.error('Error occurred:', error);
        message.error(
          t(
            CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.key,
            CREATE_REUSABLE_ASSET.SECTION_ERROR_MESSAGE.value,
          ),
        );
      }
    };

    return (
        <Container >
            <ModalBody>
                <ModalContainer >
                    <InputContainer>
                        <InputLabel>{t(CREATE_REUSABLE_ASSET.FLOW_NAME_LABEL.key, CREATE_REUSABLE_ASSET.FLOW_NAME_LABEL.value)}</InputLabel>
                        <Input
                            placeholder={t(CREATE_REUSABLE_ASSET.FLOW_NAME_PLACEHOLDER.key, CREATE_REUSABLE_ASSET.FLOW_NAME_PLACEHOLDER.value)}
                            autoComplete="off"
                            name="templateName"
                            onChange={e => { changeTempName(e.target.value) }}
                            size="large"
                            value={templateName}
                        />
                        {warningText && <WarningLabel>{warningText}</WarningLabel>}
                    </InputContainer>

                    <InputContainer>
                      <InputFlexColumn>
                        <AddNewCategory>
                          <CategoryInputLabel>{t(CREATE_REUSABLE_ASSET.SELECT_CATEGORY_LABEL.key, CREATE_REUSABLE_ASSET.SELECT_CATEGORY_LABEL.value)}</CategoryInputLabel>
                            <Popover
                                content={
                                    <PopupContainer>
                                        <PopupLabel>{t(CREATE_REUSABLE_ASSET.CATEGORY_NAME_LABEL.key, CREATE_REUSABLE_ASSET.CATEGORY_NAME_LABEL.value)}</PopupLabel>
                                        <Input
                                            placeholder={t(CREATE_REUSABLE_ASSET.CATEGORY_NAME_PLACEHOLDER.key, CREATE_REUSABLE_ASSET.CATEGORY_NAME_PLACEHOLDER.value)}
                                            autoComplete="off"
                                            name="categoryName"
                                            value={newCategoryName}
                                            onChange={e => setCategoryName(e.target.value)}
                                        />
                                        <PopupActionContainer>
                                            <CancelButton
                                                size="default"
                                                onClick={() => handlePopupVisibleChange(false)}
                                            >
                                                {t(CREATE_REUSABLE_ASSET.CANCEL_BUTTON.key, CREATE_REUSABLE_ASSET.CANCEL_BUTTON.value)}
                                            </CancelButton>
                                            <SaveButton
                                                type="primary"
                                                size="default"
                                                onClick={() => saveNewCategory()}
                                                disabled={!newCategoryName || !validCategName}
                                                loading={savingCategory}
                                            >
                                                {savingCategory ? t(CREATE_REUSABLE_ASSET.SAVING_CATEGORY_LOADING_TEXT.key, CREATE_REUSABLE_ASSET.SAVING_CATEGORY_LOADING_TEXT.value) : t(CREATE_REUSABLE_ASSET.SAVE_BUTTON.key, CREATE_REUSABLE_ASSET.SAVE_BUTTON.value)}
                                            </SaveButton>
                                        </PopupActionContainer>
                                    </PopupContainer>
                                }
                                placement="leftBottom"
                                trigger="click"
                                visible={savePopupVisible}
                                onVisibleChange={handlePopupVisibleChange}
                            >
                                <Icon type="plus" /> {t(CREATE_REUSABLE_ASSET.ADD_NEW_ACTION.key, CREATE_REUSABLE_ASSET.ADD_NEW_ACTION.value)}
                            </Popover>
                        </AddNewCategory>
                        <Select
                            loading={!allCategories}
                            showSearch
                            placeholder={t(CREATE_REUSABLE_ASSET.SELECT_CATEGORY_PLACEHOLDER.key, CREATE_REUSABLE_ASSET.SELECT_CATEGORY_PLACEHOLDER.value)}
                            optionFilterProp="children"
                            value={selectedCategoryId ? selectedCategoryId : undefined}
                            onChange={value => setSelectedCategoryId(value)}
                            filterOption={(input, option) =>
                                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {allCategories &&
                                allCategories.map(data => {
                                    return (
                                        <Option key={String(data.id)} value={data.id}>
                                            {data.name}
                                        </Option>
                                    );
                                })}
                        </Select>
                      </InputFlexColumn>
                    </InputContainer>

                    {allTeams.length > 1 && (
                        <InputContainer>
                            <InputLabel>{t(CREATE_REUSABLE_ASSET.SELECT_TEAM_LABEL.key, CREATE_REUSABLE_ASSET.SELECT_TEAM_LABEL.value)}</InputLabel>
                            <Select
                                loading={!allTeams}
                                showSearch
                                placeholder={t(CREATE_REUSABLE_ASSET.SELECT_TEAM_PLACEHOLDER.key, CREATE_REUSABLE_ASSET.SELECT_TEAM_PLACEHOLDER.value)}
                                optionFilterProp="children"
                                value={selectedGroup || undefined}
                                onChange={value => setSelectedGroup(value)}
                                filterOption={(input, option) =>
                                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {allTeams &&
                                    allTeams.map(data => {
                                        return (
                                            <Option key={String(data.id)} value={data.rgId}>
                                                {data.groupName}
                                            </Option>
                                        );
                                    })}
                            </Select>
                        </InputContainer>
                    )}
                    <AssetAction>
                        <div className='desc'>{type === "flow" ? t(
                            CREATE_REUSABLE_ASSET.REUSABLE_PAGE_DESCRIPTION.key,
                            CREATE_REUSABLE_ASSET.REUSABLE_PAGE_DESCRIPTION.value,
                        ) : t(
                            CREATE_REUSABLE_ASSET.REUSABLE_SECTION_DESCRIPTION.key,
                            CREATE_REUSABLE_ASSET.REUSABLE_SECTION_DESCRIPTION.value,
                        )} &nbsp;
                            <Tooltip placement="bottom" title={ type === 'flow' ?  t(CREATE_REUSABLE_ASSET.REUSABLE_PAGE_INFO_TOOLTIP.key,
                                CREATE_REUSABLE_ASSET.REUSABLE_PAGE_INFO_TOOLTIP.value,) : t(CREATE_REUSABLE_ASSET.REUSABLE_SECTION_INFO_TOOLTIP.key,
                                    CREATE_REUSABLE_ASSET.REUSABLE_SECTION_INFO_TOOLTIP.value,) }>
                                <Icon type="info-circle" />
                            </Tooltip>
                        </div>
                        <Switch checked={isAttach} onChange={OnSelectAttach} />
                    </AssetAction>

                    <InputContainer>
                        <div className="form_btn">
                            <Button
                                disabled={!templateName || !validTempName || !selectedCategoryId || (allTeams.length > 1 && !selectedGroup)}
                                key="submit"
                                type="primary"
                                onClick={type==='flow'? saveNewFlow : type==='block' ? saveNewSection : dummyCallback}
                                loading={savingLoader}
                            >
                                {t(
                                    CREATE_REUSABLE_ASSET.SAVE_BUTTON.key,
                                    CREATE_REUSABLE_ASSET.SAVE_BUTTON.value,
                                )}
                            </Button>
                        </div>
                    </InputContainer>

                </ModalContainer>
            </ModalBody>
        </Container>
    );
};
export default CreateReusableAsset;